"use client";

/**
 * The four things the feed shows INSTEAD of events, plus the footer.
 *
 * Split out of `EntityAuditFeed` so the container is the restore flow and the
 * list, and the copy for "nothing here / it broke / no record" lives in one
 * place where it can be read as a set. Each one is a complete answer to "why am
 * I not looking at history right now".
 */

import React from "react";
import { History, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * No record was ever identified.
 *
 * NULL IS NOT "SHOW EVERYTHING" — the same failure mode
 * DETAIL_MODAL_TAB_COVERAGE guards for nested tables, where a dropped filter
 * silently widens a per-record panel into the whole tenant.
 */
export function AuditFeedNoRecordState({
  title,
  description,
  className,
}: {
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <div
      data-testid="audit-feed-no-record"
      className={cn("flex flex-col items-center gap-2 p-8 text-center", className)}
    >
      <History aria-hidden="true" className="size-6 text-muted-foreground" />
      <p className="text-sm font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

/** The read failed and there is nothing cached to fall back on. */
export function AuditFeedErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="space-y-3 p-4">
      <p className="text-sm text-destructive">{message}</p>
      {/* ERROR_STATES_ACTIONABLE: offer the fix in place. */}
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        <RotateCcw aria-hidden="true" className="size-3.5" />
        Try again
      </Button>
    </div>
  );
}

/** The record exists and genuinely has no recorded changes. */
export function AuditFeedEmptyState() {
  return (
    <div className="flex flex-col items-center gap-2 p-8 text-center">
      <History aria-hidden="true" className="size-6 text-muted-foreground" />
      <p className="text-sm font-medium">No recorded changes yet</p>
      <p className="text-sm text-muted-foreground">
        Edits to this record will appear here as they happen.
      </p>
    </div>
  );
}

/**
 * Footer: the read-cost signal on the left, paging on the right.
 *
 * `readSealedMonths` is surfaced so the read-cost contract is observable in
 * practice and not only in tests — page 1 is one S3 GET; paging past the live
 * tail is what pays for the LIST plus the sealed months.
 */
export function AuditFeedFooter({
  readSealedMonths,
  hasNextPage,
  isFetchingNextPage,
  onLoadOlder,
}: {
  readSealedMonths: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadOlder: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border p-3">
      <span className="text-xs text-muted-foreground">
        {readSealedMonths ? "Includes archived months." : ""}
      </span>
      {hasNextPage && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onLoadOlder}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? "Loading…" : "Load older history"}
        </Button>
      )}
    </div>
  );
}
